import { Injectable } from '@angular/core';
import { EMAIL_BLACKLIST_CONFIG, getPathBlacklist, GLOBAL_EMAIL_BLACKLIST } from '../config/email-blacklist.config';

@Injectable({
  providedIn: 'root',
})
export class BlacklistUsersService {
  constructor() {}

  /**
   * Check if an email address is in the global blacklist
   * @param email - The email address to check
   * @returns boolean - True if email is blacklisted globally, false otherwise
   */
  isEmailGloballyBlacklisted(email: string): boolean {
    if (!email) {
      return false;
    }
    
    let normalizedEmail = email;
    let normalizedBlacklistedEmails = GLOBAL_EMAIL_BLACKLIST;
    
    // Apply trimming if configured
    if (EMAIL_BLACKLIST_CONFIG.trimWhitespace) {
      normalizedEmail = normalizedEmail.trim();
      normalizedBlacklistedEmails = normalizedBlacklistedEmails.map(e => e.trim());
    }
    
    // Apply case sensitivity if configured
    if (!EMAIL_BLACKLIST_CONFIG.caseSensitive) {
      normalizedEmail = normalizedEmail.toLowerCase();
      normalizedBlacklistedEmails = normalizedBlacklistedEmails.map(e => e.toLowerCase());
    }
    
    return normalizedBlacklistedEmails.includes(normalizedEmail);
  }

  /**
   * Check if an email address is blacklisted for a specific path
   * @param email - The email address to check
   * @param path - The route path to check against
   * @returns boolean - True if email is blacklisted for this path, false otherwise
   */
  isEmailBlacklistedForPath(email: string, path: string): boolean {
    if (!email) {
      return false;
    }
    
    const pathBlacklist = getPathBlacklist(path, false);
    
    if (pathBlacklist.length === 0) {
      return false;
    }
    
    let normalizedEmail = email;
    let normalizedBlacklistedEmails = pathBlacklist;
    
    // Apply trimming if configured
    if (EMAIL_BLACKLIST_CONFIG.trimWhitespace) {
      normalizedEmail = normalizedEmail.trim();
      normalizedBlacklistedEmails = normalizedBlacklistedEmails.map(e => e.trim());
    }
    
    // Apply case sensitivity if configured
    if (!EMAIL_BLACKLIST_CONFIG.caseSensitive) {
      normalizedEmail = normalizedEmail.toLowerCase();
      normalizedBlacklistedEmails = normalizedBlacklistedEmails.map(e => e.toLowerCase());
    }
    
    return normalizedBlacklistedEmails.includes(normalizedEmail);
  }

  /**
   * Check if an email is blacklisted (globally or for a specific path)
   * @param email - The email address to check
   * @param path - Optional route path to check against
   * @returns boolean - True if email is blacklisted, false otherwise
   */
  isEmailBlacklisted(email: string, path?: string): boolean {
    // Check global blacklist first
    if (this.isEmailGloballyBlacklisted(email)) {
      return true;
    }
    
    // Check path-specific blacklist if path is provided
    if (path && this.isEmailBlacklistedForPath(email, path)) {
      return true;
    }
    
    return false;
  }

  /**
   * Get the blacklist for a specific path
   * @param path - The route path to get the blacklist for
   * @returns string[] - Array of blacklisted email addresses for this path
   */
  getPathBlacklist(path: string): string[] {
    return getPathBlacklist(path, true);
  }

  /**
   * Add an email to the global blacklist
   * @param email - The email address to add
   */
  addGlobalBlacklistedEmail(email: string): void {
    if (email && !this.isEmailGloballyBlacklisted(email)) {
      GLOBAL_EMAIL_BLACKLIST.push(email.toLowerCase().trim());
    }
  }

  /**
   * Remove an email from the global blacklist
   * @param email - The email address to remove
   */
  removeGlobalBlacklistedEmail(email: string): void {
    const normalizedEmail = email.toLowerCase().trim();
    const index = GLOBAL_EMAIL_BLACKLIST.findIndex(
      blacklistedEmail => blacklistedEmail.toLowerCase().trim() === normalizedEmail
    );
    
    if (index > -1) {
      GLOBAL_EMAIL_BLACKLIST.splice(index, 1);
    }
  }

  /**
   * Add an email to a path-specific blacklist
   * @param email - The email address to add
   * @param path - The route path to restrict
   */
  addPathBlacklistedEmail(email: string, path: string): void {
    if (email && !this.isEmailBlacklistedForPath(email, path)) {
      if (!EMAIL_BLACKLIST_CONFIG.pathBlacklists[path]) {
        EMAIL_BLACKLIST_CONFIG.pathBlacklists[path] = [];
      }
      EMAIL_BLACKLIST_CONFIG.pathBlacklists[path].push(email.toLowerCase().trim());
    }
  }

  /**
   * Remove an email from a path-specific blacklist
   * @param email - The email address to remove
   * @param path - The route path to unrestrict
   */
  removePathBlacklistedEmail(email: string, path: string): void {
    if (!EMAIL_BLACKLIST_CONFIG.pathBlacklists[path]) {
      return;
    }
    
    const normalizedEmail = email.toLowerCase().trim();
    EMAIL_BLACKLIST_CONFIG.pathBlacklists[path] = EMAIL_BLACKLIST_CONFIG.pathBlacklists[path].filter(
      blacklistedEmail => blacklistedEmail.toLowerCase().trim() !== normalizedEmail
    );
  }
}
